import { Alert, Button, Group, Stack, Text } from '@mantine/core';
import { IconEye } from '@tabler/icons-react';
import type { CustomerContact } from '../../lib/api.customer';
import { formatTime } from '../../lib/formatters';
import { PORTAL_MODULE_LABELS, PORTAL_ROLE_LABELS } from '../../lib/portalModuleLabels';
import { usePortalModules } from './portalModules';

/**
 * Leiste der Mitarbeiter-Ansicht. Wer das Portal in fremder Sicht offen hat, darf das
 * keinen Moment vergessen — sonst hält man gesperrte Knöpfe für einen Fehler oder die
 * eigene Sicht für die des Kunden.
 */
export function PreviewBanner({
  preview,
  contactName,
  companyName,
  onEnd,
}: {
  preview: NonNullable<CustomerContact['preview']>;
  contactName: string | null;
  companyName?: string | null;
  onEnd: () => void;
}) {
  const who = contactName ?? 'Ansprechpartner';
  // „Vorschau mit dieser Auswahl" beim Freischalten: nicht zwingend die Rechte des Kontakts.
  const assumedRole = preview.assumedRole ?? null;
  // Die Bereiche, die sich aus der Auswahl ergeben — der Server hat sie für diese Sitzung angenommen.
  const openAreas = usePortalModules();

  return (
    <Alert
      color="orange"
      variant="filled"
      radius="md"
      icon={<IconEye size={20} />}
      title={`Mitarbeiter-Ansicht: ${who}${companyName ? ` · ${companyName}` : ''}`}
    >
      <Group justify="space-between" align="flex-end" gap="sm" wrap="wrap">
        <Stack gap={2} style={{ flex: 1, minWidth: 240 }}>
          <Text size="sm">
            {assumedRole
              ? `So sähe ${who} das Portal mit der gewählten Auswahl.`
              : `So sieht ${who} das Portal.`}{' '}
            Nur ansehen — Absenden und Ändern sind gesperrt, beim Kunden kommt nichts an.
          </Text>
          {assumedRole && (
            <Text size="sm" fw={600}>
              Angenommen: {PORTAL_ROLE_LABELS[assumedRole] ?? assumedRole} ·{' '}
              {openAreas.length > 0
                ? `Bereiche: ${openAreas.map((m) => PORTAL_MODULE_LABELS[m] ?? m).join(', ')}`
                : 'kein Bereich offen'}{' '}
              — nicht zwingend das, was {who} heute hat.
            </Text>
          )}
          {!preview.customerPortalEnabled ? (
            <Text size="sm">
              Das Portal ist für diesen Kunden noch nicht freigeschaltet — der Kunde selbst sieht es
              noch nicht.
            </Text>
          ) : (
            !preview.contactPortalEnabled && (
              <Text size="sm">
                {who} hat noch keinen eigenen Portal-Zugang — so sähe es nach der Freischaltung aus.
              </Text>
            )
          )}
          <Text size="xs" style={{ opacity: 0.85 }}>
            Geöffnet von {preview.byName} · endet um {formatTime(preview.expiresAt)} Uhr
          </Text>
        </Stack>
        <Button size="xs" variant="white" color="orange" onClick={onEnd}>
          Ansicht beenden
        </Button>
      </Group>
    </Alert>
  );
}
